import { trackGa4Checkout } from '../ga4';
import { v4 as uuidv4 } from 'uuid';
import {
    addMarketplaceCartItemFn,
    Analytics,
    checkoutMarketplaceCartFn,
    getMarketplaceCartFn,
    removeMarketplaceCartItemFn,
} from '../api';
import { deleteCookie, getCookie, setCookie } from '../utils';
import {
    buildEmptyCartSnapshot,
    buildMarketplaceCartUrl,
    buildPersistedCartPayload,
    calculateLocalSubtotal,
    CART_COOKIE_NAME,
    CART_COOKIE_SIZE_LIMIT,
    CART_LINE_LIMIT,
    CART_SYNC_STATES,
    decodeCartCookiePayload,
    encodeCartCookiePayload,
    formatCurrency,
    getCartLineKey,
    getCartLineSignature,
    isPrimaryProductioncrateHost,
    LEGACY_CART_COOKIE_NAMES,
    normalizeAddItemInput,
    normalizeCartDisplay,
    normalizeCurrency,
} from './utils';

const SYNC_DEBOUNCE_MS = 400;
const CART_SYNC_CHANNEL_NAME = 'crate:marketplace-cart-sync';
const CART_SYNC_STORAGE_KEY = 'crate:marketplace-cart-sync';
const EVENT_NAMES = {
    READY: 'crate:cart:ready',
    CHANGED: 'crate:cart:changed',
    SYNC_STATE: 'crate:cart:sync-state',
    MERGE_REQUIRED: 'crate:cart:merge-required',
    OPENED: 'crate:cart:opened',
    CLOSED: 'crate:cart:closed',
};
const ANALYTICS_EVENT_NAMES = {
    ADD_TO_CART: 'addToCart',
    REMOVE_FROM_CART: 'removeFromCart',
    CHECKOUT: 'checkout',
};

const getAnalyticsLineMetadata = (line) => ({
    item_type: line?.type,
    ...(line?.assetUuid ? { asset_uuid: line.assetUuid } : {}),
    ...(line?.bundleId ? { bundle_id: line.bundleId } : {}),
    ...(line?.display?.amount != null ? { amount: line.display.amount } : {}),
    ...(line?.display?.currency ? { currency: line.display.currency } : {}),
});

const parsePositiveInteger = (value) => {
    const parsedValue = Number.parseInt(String(value ?? ''), 10);
    if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
        return null;
    }
    return parsedValue;
};

const parseNonNegativeInteger = (value) => {
    const parsedValue = Number.parseInt(String(value ?? ''), 10);
    if (!Number.isInteger(parsedValue) || parsedValue < 0) {
        return 0;
    }
    return parsedValue;
};

const mapLinesByKey = (lines) => {
    const lineMap = new Map();

    (Array.isArray(lines) ? lines : []).forEach((line) => {
        const lineKey = getCartLineKey(line);
        if (lineKey) {
            lineMap.set(lineKey, line);
        }
    });

    return lineMap;
};

const cloneDisplay = (display) => {
    if (!display || typeof display !== 'object') {
        return null;
    }

    return { ...display };
};

const mergeDisplay = (serverDisplay, localDisplay) => {
    const serverDisplayClone = cloneDisplay(serverDisplay);
    const localDisplayClone = cloneDisplay(localDisplay);

    if (!serverDisplayClone && !localDisplayClone) {
        return null;
    }

    return {
        ...localDisplayClone,
        ...serverDisplayClone,
        previewUrl: serverDisplayClone?.previewUrl || localDisplayClone?.previewUrl || null,
        waveformUrl: serverDisplayClone?.waveformUrl || localDisplayClone?.waveformUrl || null,
    };
};

const cloneLine = (line) => {
    if (!line || typeof line !== 'object') {
        return null;
    }

    return {
        ...line,
        display: cloneDisplay(line.display),
    };
};

const cloneLines = (lines) => (Array.isArray(lines) ? lines.map((line) => cloneLine(line)).filter(Boolean) : []);

const compareSyncTuples = (leftRevision, leftWriterId, rightRevision, rightWriterId) => {
    const normalizedLeftRevision = Number.isInteger(leftRevision) ? leftRevision : 0;
    const normalizedRightRevision = Number.isInteger(rightRevision) ? rightRevision : 0;

    if (normalizedLeftRevision !== normalizedRightRevision) {
        return normalizedLeftRevision - normalizedRightRevision;
    }

    return String(leftWriterId ?? '').localeCompare(String(rightWriterId ?? ''));
};

const normalizeServerLine = (item, previousLineMap) => {
    if (!item || typeof item !== 'object') {
        return null;
    }

    const type = item.line_type === 'bundle' ? 'bundle' : 'asset';
    const previousLine = previousLineMap.get(type === 'bundle' ? `bundle:${item?.bundle?.id}` : `asset:${item?.asset?.uuid}`);
    const selectedPrice = item.selected_price || {};
    const amount = Number(selectedPrice.amount);
    const currency = normalizeCurrency(selectedPrice.currency);
    const previousAssetHref = previousLine?.assetHref || null;
    const display = normalizeCartDisplay({
        name: String(item?.asset?.name || item?.bundle?.name || previousLine?.display?.name || 'Untitled item'),
        amount: Number.isFinite(amount) ? amount : null,
        currency,
        priceLabel: formatCurrency(amount, currency),
        vertical: item?.asset?.vertical
            ?? item?.asset?.main_category
            ?? item?.asset?.mainCategory
            ?? item?.bundle?.vertical
            ?? item?.bundle?.main_category
            ?? item?.bundle?.mainCategory
            ?? previousLine?.display?.vertical,
        previewUrl: item?.preview_url
            ?? item?.previewUrl
            ?? item?.display?.preview_url
            ?? item?.display?.previewUrl
            ?? item?.asset?.preview_url
            ?? item?.asset?.previewUrl
            ?? item?.asset?.thumbnail_url
            ?? item?.asset?.thumbnailUrl
            ?? item?.asset?.image_url
            ?? item?.asset?.imageUrl
            ?? item?.asset?.poster_url
            ?? item?.asset?.posterUrl
            ?? item?.bundle?.preview_url
            ?? item?.bundle?.previewUrl
            ?? item?.bundle?.thumbnail_url
            ?? item?.bundle?.thumbnailUrl
            ?? item?.bundle?.image_url
            ?? item?.bundle?.imageUrl
            ?? previousLine?.display?.previewUrl,
        waveformUrl: item?.waveform_url
            ?? item?.waveformUrl
            ?? item?.display?.waveform_url
            ?? item?.display?.waveformUrl
            ?? item?.asset?.waveform_url
            ?? item?.asset?.waveformUrl
            ?? item?.bundle?.waveform_url
            ?? item?.bundle?.waveformUrl
            ?? previousLine?.display?.waveformUrl,
        previews: item?.display?.previews ?? item?.asset?.previews ?? item?.bundle?.previews,
    }, type, { assetHref: previousAssetHref });

    if (type === 'asset') {
        const assetUuid = String(item?.asset?.uuid ?? '').trim();
        if (!assetUuid) {
            return null;
        }

        return {
            type,
            assetUuid,
            bundleId: null,
            assetHref: previousAssetHref,
            assetPriceId: String(selectedPrice.source_type ?? '') === 'asset_price'
                ? parsePositiveInteger(selectedPrice.source_id)
                : null,
            addedAt: previousLine?.addedAt || new Date().toISOString(),
            display,
            serverLineId: parsePositiveInteger(item.id),
        };
    }

    const bundleId = parsePositiveInteger(item?.bundle?.id);
    if (!bundleId) {
        return null;
    }

    return {
        type,
        bundleId,
        assetUuid: null,
        assetPriceId: null,
        addedAt: previousLine?.addedAt || new Date().toISOString(),
        display,
        serverLineId: parsePositiveInteger(item.id),
    };
};

const normalizeServerCart = (cart, previousLines = []) => {
    const previousLineMap = mapLinesByKey(previousLines);
    const lines = Array.isArray(cart?.items)
        ? cart.items.map((item) => normalizeServerLine(item, previousLineMap)).filter(Boolean)
        : [];
    const subtotalAmount = Number(cart?.subtotal_amount);
    const subtotalCurrency = normalizeCurrency(cart?.currency);

    return {
        id: parsePositiveInteger(cart?.id),
        lines,
        subtotal: {
            amount: Number.isFinite(subtotalAmount) ? subtotalAmount : null,
            currency: subtotalCurrency,
            formatted: Number.isFinite(subtotalAmount) && subtotalCurrency
                ? formatCurrency(subtotalAmount, subtotalCurrency)
                : null,
        },
    };
};

const normalizeServerCartResponse = (response, previousLines = []) => {
    const rawCart = response?.cart && typeof response.cart === 'object'
        ? response.cart
        : response;

    return {
        cart: normalizeServerCart(rawCart, previousLines),
        version: parseNonNegativeInteger(response?.version),
        analytics: response?.analytics ?? null,
        checkoutUrl: typeof response?.checkout_url === 'string' && response.checkout_url.trim()
            ? response.checkout_url.trim()
            : null,
    };
};

const isCartVersionConflictError = (error) => String(error?.code ?? '').trim().toLowerCase() === 'cart_version_conflict';

const normalizeConflictEnvelope = (error, previousLines = []) => {
    if (!isCartVersionConflictError(error)) {
        return null;
    }

    return normalizeServerCartResponse({
        cart: error?.cart,
        version: error?.version,
    }, previousLines);
};

const buildMergeCartDetails = (lines, subtotal = calculateLocalSubtotal(lines)) => ({
    itemCount: Array.isArray(lines) ? lines.length : 0,
    subtotal,
    lines: cloneLines(lines).map((line) => ({
        lineKey: getCartLineKey(line),
        type: line.type,
        assetUuid: line.assetUuid || undefined,
        bundleId: line.bundleId || undefined,
        display: line.display || undefined,
    })),
});

const shouldPromptMerge = (localLines, serverLines) => {
    if (!localLines.length || !serverLines.length) {
        return false;
    }

    const localSignatures = new Set(localLines.map((line) => getCartLineSignature(line)).filter(Boolean));
    const serverSignatures = new Set(serverLines.map((line) => getCartLineSignature(line)).filter(Boolean));

    if (localSignatures.size !== serverSignatures.size) {
        return true;
    }

    for (const signature of localSignatures) {
        if (!serverSignatures.has(signature)) {
            return true;
        }
    }

    return false;
};

const haveSameLineSet = (localLines, serverLines) => {
    if (localLines.length !== serverLines.length) {
        return false;
    }

    return !shouldPromptMerge(localLines, serverLines);
};

const hasServerDisplayMetadata = (line) => Boolean(line?.display?.previewUrl || line?.display?.waveformUrl);

const getServerDisplayMetadataSignature = (line) => [
    line?.display?.previewUrl || '',
    line?.display?.waveformUrl || '',
].join('|');

const hasServerDisplayMetadataMismatch = (desiredLine, serverLine) => (
    hasServerDisplayMetadata(desiredLine)
    && getServerDisplayMetadataSignature(desiredLine) !== getServerDisplayMetadataSignature(serverLine)
);

const isInvalidAssetPriceError = (error) => {
    const fieldErrors = error?.errors?.asset_price_id;
    const message = String(error?.message || '').toLowerCase();

    return (Array.isArray(fieldErrors) && fieldErrors.length > 0)
        || message.includes('asset price id');
};

const getPersistedCartCookieNames = () => [CART_COOKIE_NAME, ...LEGACY_CART_COOKIE_NAMES]
    .filter(Boolean)
    .filter((cookieName, index, cookieNames) => cookieNames.indexOf(cookieName) === index);

const clearPersistedCartCookies = () => {
    getPersistedCartCookieNames().forEach((cookieName) => {
        deleteCookie(cookieName);
    });
};

const writePersistedCartCookie = (encodedPayload) => {
    clearPersistedCartCookies();
    setCookie(CART_COOKIE_NAME, encodedPayload, 30);
};

const readPersistedCartCookie = () => {
    for (const cookieName of getPersistedCartCookieNames()) {
        const encodedPayload = getCookie(cookieName);
        const persistedState = decodeCartCookiePayload(encodedPayload);

        if (persistedState) {
            return {
                cookieName,
                encodedPayload,
                persistedState,
            };
        }
    }

    return {
        cookieName: null,
        encodedPayload: null,
        persistedState: null,
    };
};

class MarketplaceCartController {
    constructor(windowObject = window) {
        this.window = windowObject;
        this.subscribers = new Set();
        this.ready = false;
        this.isOpen = false;
        this.syncTimer = null;
        this.pendingServerRemovals = new Set();
        this.pendingSyncRequest = null;
        this.syncInFlightPromise = null;
        this.session = {
            user: null,
            isAuthenticating: true,
        };
        this.lastResolvedUserKey = null;
        this.mergeState = null;
        this.mergeRequired = false;
        this.lastError = null;
        this.serverCart = null;
        this.serverVersion = 0;
        this.sessionResolutionPromise = null;
        this.sessionVersion = 0;
        this.isClearingAfterCheckout = false;
        this.checkoutIntentPending = false;
        this.isReconciling = false;
        this.instanceWriterId = uuidv4();
        this.broadcastChannel = null;

        const persistedCartCookie = readPersistedCartCookie();
        const persistedState = persistedCartCookie.persistedState;
        this.clientCartId = persistedState?.clientCartId || uuidv4();
        this.updatedAt = persistedState?.updatedAt || null;
        this.revision = Number.isInteger(persistedState?.revision) ? persistedState.revision : 0;
        this.writerId = persistedState?.writerId || this.instanceWriterId;
        this.ownerUserId = persistedState?.ownerUserId || null;
        this.syncState = persistedState?.syncState || CART_SYNC_STATES.CLEAN;
        this.lines = cloneLines(persistedState?.lines).map((line) => ({
            ...line,
            serverLineId: null,
        }));
        this.subtotal = calculateLocalSubtotal(this.lines);

        this.handleWindowFocus = this.handleWindowFocus.bind(this);
        this.handleWindowOnline = this.handleWindowOnline.bind(this);
        this.handleStorageEvent = this.handleStorageEvent.bind(this);
        this.handleBroadcastMessage = this.handleBroadcastMessage.bind(this);

        this.window.addEventListener('focus', this.handleWindowFocus);
        this.window.addEventListener('online', this.handleWindowOnline);
        this.window.addEventListener('storage', this.handleStorageEvent);
        this.broadcastChannel = this.createBroadcastChannel();

        if (persistedCartCookie.encodedPayload && persistedCartCookie.cookieName !== CART_COOKIE_NAME) {
            writePersistedCartCookie(persistedCartCookie.encodedPayload);
        }
    }

    destroy() {
        this.window.removeEventListener('focus', this.handleWindowFocus);
        this.window.removeEventListener('online', this.handleWindowOnline);
        this.window.removeEventListener('storage', this.handleStorageEvent);
        this.clearSyncTimer();
        if (this.broadcastChannel && typeof this.broadcastChannel.removeEventListener === 'function') {
            this.broadcastChannel.removeEventListener('message', this.handleBroadcastMessage);
        }
        if (this.broadcastChannel && typeof this.broadcastChannel.close === 'function') {
            this.broadcastChannel.close();
        }
        this.subscribers.clear();
    }

    markReady() {
        if (this.ready) {
            return;
        }

        this.ready = true;
        this.dispatchEvent(EVENT_NAMES.READY, {
            snapshot: this.getSnapshot(),
        });
    }

    getSnapshot() {
        return {
            apiVersion: 1,
            isReady: this.ready,
            isOpen: this.isOpen,
            clientCartId: this.clientCartId,
            revision: this.revision,
            updatedAt: this.updatedAt,
            syncState: this.syncState,
            isSessionResolved: this.isSessionResolved(),
            isReconciling: this.isReconciling,
            itemCount: this.lines.length,
            lines: this.lines.map((line) => ({
                lineKey: getCartLineKey(line),
                type: line.type,
                assetUuid: line.assetUuid || undefined,
                bundleId: line.bundleId || undefined,
                assetHref: line.assetHref || undefined,
                assetPriceId: line.assetPriceId || undefined,
                addedAt: line.addedAt,
                display: line.display || undefined,
            })),
            subtotal: this.subtotal,
            mergeRequired: this.mergeRequired,
            mergeComparison: this.buildMergeComparisonSnapshot(),
            lastError: this.lastError,
        };
    }

    subscribe(listener) {
        if (typeof listener !== 'function') {
            return () => {};
        }

        this.subscribers.add(listener);
        listener(this.getSnapshot());

        return () => {
            this.subscribers.delete(listener);
        };
    }

    getSessionUserKey(user = this.session.user) {
        return user?.id || user?.email || null;
    }

    getSessionOwnerUserId(user = this.session.user) {
        const ownerUserId = String(user?.id ?? '').trim();
        return ownerUserId || null;
    }

    isSessionResolved() {
        return !this.session.isAuthenticating && !this.isReconciling && !this.sessionResolutionPromise;
    }

    buildMergeComparisonSnapshot() {
        if (!this.mergeState) {
            return null;
        }

        return {
            localCart: buildMergeCartDetails(
                this.mergeState.localLines,
                calculateLocalSubtotal(this.mergeState.localLines)
            ),
            accountCart: buildMergeCartDetails(
                this.mergeState.serverLines,
                this.mergeState.serverCart?.subtotal || calculateLocalSubtotal(this.mergeState.serverLines)
            ),
        };
    }

    updateServerStateFromEnvelope(serverEnvelope) {
        if (!serverEnvelope) {
            return null;
        }

        this.serverVersion = parseNonNegativeInteger(serverEnvelope.version);
        if (serverEnvelope.cart) {
            this.serverCart = serverEnvelope.cart;
        }

        return serverEnvelope;
    }

    getCurrentServerVersion() {
        return parseNonNegativeInteger(this.serverVersion);
    }

    async fetchServerCartEnvelope(previousLines = this.lines) {
        const serverEnvelope = normalizeServerCartResponse(await getMarketplaceCartFn(), previousLines);
        this.updateServerStateFromEnvelope(serverEnvelope);
        return serverEnvelope;
    }

    applyVersionConflict(error, previousLines = this.lines) {
        const conflictEnvelope = normalizeConflictEnvelope(error, previousLines);
        if (!conflictEnvelope) {
            return null;
        }

        this.updateServerStateFromEnvelope(conflictEnvelope);
        return conflictEnvelope;
    }

    async removeServerLineById(serverLineId, previousLines = this.lines) {
        const serverEnvelope = normalizeServerCartResponse(
            await removeMarketplaceCartItemFn(serverLineId, {
                version: this.getCurrentServerVersion(),
                idempotencyKey: uuidv4(),
            }),
            previousLines
        );

        this.updateServerStateFromEnvelope(serverEnvelope);
        return serverEnvelope;
    }

    buildServerDisplayPayload(line) {
        const previewUrl = line?.display?.previewUrl;
        const waveformUrl = line?.display?.waveformUrl;
        const display = {};

        if (previewUrl) {
            display.previewUrl = previewUrl;
            display.preview_url = previewUrl;
        }

        if (waveformUrl) {
            display.waveformUrl = waveformUrl;
            display.waveform_url = waveformUrl;
        }

        return Object.keys(display).length ? display : undefined;
    }

    async addAssetLineToServer(localLine) {
        const payload = {
            asset_uuid: localLine.assetUuid,
            asset_price_id: localLine.assetPriceId || undefined,
        };
        const display = this.buildServerDisplayPayload(localLine);
        if (display) {
            payload.display = display;
        }

        try {
            const serverEnvelope = normalizeServerCartResponse(
                await addMarketplaceCartItemFn(payload, {
                    version: this.getCurrentServerVersion(),
                    idempotencyKey: uuidv4(),
                }),
                this.lines
            );
            this.updateServerStateFromEnvelope(serverEnvelope);
            return serverEnvelope;
        } catch (error) {
            if (!payload.asset_price_id || !isInvalidAssetPriceError(error)) {
                throw error;
            }

            const serverEnvelope = normalizeServerCartResponse(
                await addMarketplaceCartItemFn({
                    asset_uuid: localLine.assetUuid,
                    ...(display ? { display } : {}),
                }, {
                    version: this.getCurrentServerVersion(),
                    idempotencyKey: uuidv4(),
                }),
                this.lines
            );
            this.updateServerStateFromEnvelope(serverEnvelope);
            return serverEnvelope;
        }
    }

    async addBundleLineToServer(localLine) {
        const display = this.buildServerDisplayPayload(localLine);
        const serverEnvelope = normalizeServerCartResponse(
            await addMarketplaceCartItemFn({
                bundle_id: localLine.bundleId,
                ...(display ? { display } : {}),
            }, {
                version: this.getCurrentServerVersion(),
                idempotencyKey: uuidv4(),
            }),
            this.lines
        );

        this.updateServerStateFromEnvelope(serverEnvelope);
        return serverEnvelope;
    }

    touchRevision(nextOwnerUserId = this.ownerUserId) {
        this.revision += 1;
        this.writerId = this.instanceWriterId;
        this.ownerUserId = nextOwnerUserId || null;
    }

    captureSyncState() {
        return {
            revision: this.revision,
            sessionVersion: this.sessionVersion,
            userKey: this.getSessionUserKey(),
            lines: cloneLines(this.lines),
            pendingServerRemovals: new Set(this.pendingServerRemovals),
        };
    }

    isCurrentSyncCapture(syncCapture) {
        return this.isCurrentAuthenticatedSession(syncCapture?.userKey, syncCapture?.sessionVersion)
            && this.revision === syncCapture?.revision;
    }

    setSession({ user, isAuthenticating }) {
        const previousUserKey = this.getSessionUserKey();
        const previousIsAuthenticating = this.session.isAuthenticating;
        const nextUserKey = this.getSessionUserKey(user);
        const nextIsAuthenticating = Boolean(isAuthenticating);

        this.session = {
            user: user || null,
            isAuthenticating: nextIsAuthenticating,
        };

        if (previousUserKey !== nextUserKey || previousIsAuthenticating !== nextIsAuthenticating) {
            this.sessionVersion += 1;
        }

        if (!this.session.isAuthenticating && nextUserKey !== this.lastResolvedUserKey) {
            this.lastResolvedUserKey = nextUserKey;

            if (!nextUserKey) {
                this.sessionResolutionPromise = null;
                this.isReconciling = false;
                this.clearForLogout('session-logout');
                return;
            }

            const sessionVersion = this.sessionVersion;
            this.isReconciling = true;
            this.notifySubscribers('session-reconcile-start');
            const sessionResolutionPromise = this.handleResolvedSession(nextUserKey, sessionVersion).catch((error) => {
                console.log('Error while reconciling marketplace cart session', error);
            }).finally(() => {
                if (this.sessionResolutionPromise === sessionResolutionPromise) {
                    this.sessionResolutionPromise = null;
                }
                if (this.sessionVersion === sessionVersion) {
                    this.isReconciling = false;
                    this.notifySubscribers('session-reconcile-finish');
                }
            });
            this.sessionResolutionPromise = sessionResolutionPromise;
            return;
        }

        if (!this.session.isAuthenticating && !nextUserKey && previousIsAuthenticating) {
            this.notifySubscribers('session-resolved');
        }
    }

    async waitForSessionResolution() {
        if (this.isSessionResolved()) {
            return this.getSnapshot();
        }

        if (this.sessionResolutionPromise) {
            await this.sessionResolutionPromise;
            return this.getSnapshot();
        }

        return new Promise((resolve) => {
            const unsubscribe = this.subscribe((snapshot) => {
                if (!this.isSessionResolved()) {
                    return;
                }

                unsubscribe();
                resolve(snapshot);
            });
        });
    }

    clearForLogout(reason = 'session-logout') {
        this.sessionVersion += 1;
        this.session = {
            user: null,
            isAuthenticating: false,
        };
        this.lastResolvedUserKey = null;
        this.sessionResolutionPromise = null;
        this.isReconciling = false;
        this.clearSyncTimer();
        this.pendingServerRemovals.clear();
        this.pendingSyncRequest = null;
        this.clientCartId = uuidv4();
        this.touchRevision(null);
        this.updatedAt = null;
        this.syncState = CART_SYNC_STATES.CLEAN;
        this.lines = [];
        this.subtotal = calculateLocalSubtotal(this.lines);
        this.ownerUserId = null;
        this.mergeRequired = false;
        this.mergeState = null;
        this.serverCart = null;
        this.serverVersion = 0;
        this.lastError = null;
        this.checkoutIntentPending = false;
        clearPersistedCartCookies();
        this.publishCartMessage({
            type: 'clear',
            clientCartId: this.clientCartId,
            revision: this.revision,
            writerId: this.writerId,
            ownerUserId: null,
        });
        this.notifySubscribers(reason);
    }

    async prepareForLogout(options = {}) {
        const normalizedOptions = typeof options === 'string'
            ? { reason: options }
            : (options || {});
        const { reason = 'logout', user = null } = normalizedOptions;

        if (!this.session.user && user) {
            this.sessionVersion += 1;
            this.session = {
                user,
                isAuthenticating: false,
            };
            this.lastResolvedUserKey = user?.id || user?.email || null;
        }

        const shouldSyncBeforeLogout = this.isPrimaryHost()
            && Boolean(this.session.user)
            && !this.session.isAuthenticating
            && !this.mergeRequired
            && this.lines.length > 0;

        if (shouldSyncBeforeLogout) {
            try {
                await this.performSync(`${reason}-sync`, true);
            } catch (error) {
                console.log('Error while syncing marketplace cart before logout', error);
            }
        }

        this.clearForLogout(reason);
        return this.getSnapshot();
    }

    async clearAfterCheckout(reason = 'checkout-complete') {
        this.sessionVersion += 1;
        this.sessionResolutionPromise = null;
        this.isReconciling = false;
        this.isClearingAfterCheckout = true;
        this.clearSyncTimer();

        if (this.isPrimaryHost()) {
            try {
                const serverEnvelope = await this.fetchServerCartEnvelope(this.lines);
                const serverCart = serverEnvelope.cart;
                const serverLineIds = new Set(
                    serverCart.lines
                        .map((line) => line.serverLineId)
                        .filter(Boolean)
                );

                for (const serverLineId of serverLineIds) {
                    await this.removeServerLineById(serverLineId, this.lines);
                }
            } catch (error) {
                console.log('Error while clearing marketplace cart after checkout', error);
            }
        }

        this.pendingServerRemovals.clear();
        this.pendingSyncRequest = null;
        this.clientCartId = uuidv4();
        this.touchRevision(null);
        this.updatedAt = null;
        this.syncState = CART_SYNC_STATES.CLEAN;
        this.lines = [];
        this.subtotal = calculateLocalSubtotal(this.lines);
        this.ownerUserId = null;
        this.mergeRequired = false;
        this.mergeState = null;
        this.serverCart = null;
        this.serverVersion = 0;
        this.lastError = null;
        this.isClearingAfterCheckout = false;
        this.checkoutIntentPending = false;
        clearPersistedCartCookies();
        this.publishCartMessage({
            type: 'clear',
            clientCartId: this.clientCartId,
            revision: this.revision,
            writerId: this.writerId,
            ownerUserId: null,
        });
        this.notifySubscribers(reason);
        return this.getSnapshot();
    }

    async addItem(input) {
        const normalizedInput = normalizeAddItemInput(input);
        if (!normalizedInput) {
            throw new Error('Invalid cart input');
        }

        if (!this.isPrimaryHost()) {
            this.window.location.assign(this.buildSecondaryDomainIntakeUrl(normalizedInput));
            return this.getSnapshot();
        }

        const lineKey = getCartLineKey(normalizedInput);
        const existingLineMap = mapLinesByKey(this.lines);
        const existingLine = existingLineMap.get(lineKey);

        if (!existingLine && this.lines.length >= CART_LINE_LIMIT) {
            this.setNonBlockingError('Cart is full.');
            this.notifySubscribers('cart-full');
            return this.getSnapshot();
        }

        const nextLine = {
            ...existingLine,
            ...normalizedInput,
            addedAt: existingLine?.addedAt || normalizedInput.addedAt,
            serverLineId: existingLine?.serverLineId || null,
            assetHref: normalizedInput.assetHref || existingLine?.assetHref || null,
            display: normalizedInput.display || existingLine?.display || null,
        };

        const nextLines = existingLine
            ? this.lines.map((line) => (getCartLineKey(line) === lineKey ? nextLine : line))
            : [...this.lines, nextLine];

        const previousRevision = this.revision;
        const previousWriterId = this.writerId;
        const previousOwnerUserId = this.ownerUserId;
        this.touchRevision(this.getSessionOwnerUserId() || this.ownerUserId);
        this.pendingServerRemovals.delete(lineKey);

        if (!this.persistLines(nextLines, CART_SYNC_STATES.DIRTY)) {
            this.revision = previousRevision;
            this.writerId = previousWriterId;
            this.ownerUserId = previousOwnerUserId;
            this.setNonBlockingError('Cart is full.');
            this.notifySubscribers('cart-full');
            return this.getSnapshot();
        }

        this.lines = nextLines;
        this.syncState = CART_SYNC_STATES.DIRTY;
        this.lastError = null;
        this.updatedAt = new Date().toISOString();
        this.subtotal = calculateLocalSubtotal(this.lines);

        Analytics.sendCustomEvent(ANALYTICS_EVENT_NAMES.ADD_TO_CART, getAnalyticsLineMetadata(normalizedInput));

        if (normalizedInput.sourceContext?.explicitPurchase) {
            this.setOpenState(true, 'item-added');
        }

        this.notifySubscribers('item-added');
        this.scheduleSync('item-added');

        return this.getSnapshot();
    }

    async removeItem(lineKey) {
        const normalizedLineKey = String(lineKey ?? '').trim();
        const existingLine = this.lines.find((line) => getCartLineKey(line) === normalizedLineKey);

        if (!existingLine) {
            return this.getSnapshot();
        }

        if (existingLine.serverLineId) {
            this.pendingServerRemovals.add(normalizedLineKey);
        } else if (this.serverCart?.lines?.some((line) => getCartLineKey(line) === normalizedLineKey)) {
            this.pendingServerRemovals.add(normalizedLineKey);
        }

        const nextLines = this.lines.filter((line) => getCartLineKey(line) !== normalizedLineKey);

        const previousRevision = this.revision;
        const previousWriterId = this.writerId;
        const previousOwnerUserId = this.ownerUserId;
        this.touchRevision(this.getSessionOwnerUserId() || this.ownerUserId);

        if (!this.persistLines(nextLines, CART_SYNC_STATES.DIRTY)) {
            this.revision = previousRevision;
            this.writerId = previousWriterId;
            this.ownerUserId = previousOwnerUserId;
            this.setNonBlockingError('We could not update the cart right now.');
            this.notifySubscribers('remove-failed');
            return this.getSnapshot();
        }

        this.lines = nextLines;
        this.syncState = CART_SYNC_STATES.DIRTY;
        this.lastError = null;
        this.updatedAt = new Date().toISOString();
        this.subtotal = calculateLocalSubtotal(this.lines);
        Analytics.sendCustomEvent(ANALYTICS_EVENT_NAMES.REMOVE_FROM_CART, getAnalyticsLineMetadata(existingLine));
        this.notifySubscribers('item-removed');
        this.scheduleSync('item-removed');

        return this.getSnapshot();
    }

    async open() {
        if (this.setOpenState(true, 'drawer-open')) {
            this.notifySubscribers('drawer-open');
        }

        if (this.canSyncInBackground() && [CART_SYNC_STATES.DIRTY, CART_SYNC_STATES.ERROR].includes(this.syncState)) {
            this.scheduleSync('drawer-open', true);
        }

        return this.getSnapshot();
    }

    async close() {
        if (this.isOpen) {
            this.setOpenState(false, 'drawer-close');
            this.notifySubscribers('drawer-close');
        }

        return this.getSnapshot();
    }

    async sync({ reason = 'manual' } = {}) {
        return this.performSync(reason, false);
    }

    async checkout() {
        if (!this.session.user) {
            this.window.location.assign('/login?return_to=/marketplace/cart&cart_intent=checkout');
            return false;
        }

        this.checkoutIntentPending = true;

        if (this.mergeRequired) {
            this.setOpenState(true, 'checkout-merge-required');
            this.notifySubscribers('checkout-merge-required');
            return false;
        }

        if (this.sessionResolutionPromise) {
            await this.sessionResolutionPromise;
        }

        if (this.mergeRequired) {
            this.setOpenState(true, 'checkout-merge-required');
            this.notifySubscribers('checkout-merge-required');
            return false;
        }

        try {
            await this.performSync('checkout', true);
        } catch (error) {
            this.checkoutIntentPending = false;
            return false;
        }

        try {
            const response = normalizeServerCartResponse(await checkoutMarketplaceCartFn({
                version: this.getCurrentServerVersion(),
                idempotencyKey: uuidv4(),
            }), this.lines);
            this.updateServerStateFromEnvelope(response);

            if (response.checkoutUrl) {
                trackGa4Checkout(response.analytics);
                Analytics.sendCustomEvent(ANALYTICS_EVENT_NAMES.CHECKOUT, {
                    item_count: this.lines.length,
                    subtotal_amount: this.subtotal.amount,
                    currency: this.subtotal.currency,
                });
                this.checkoutIntentPending = false;
                this.window.location.assign(response.checkoutUrl);
                return true;
            }
        } catch (error) {
            this.applyVersionConflict(error, this.lines);
        }

        this.checkoutIntentPending = false;
        return false;
    }

    async resolveMerge(action) {
        if (!this.mergeRequired || !this.mergeState) {
            return this.getSnapshot();
        }

        const shouldResumeCheckout = this.checkoutIntentPending;
        const normalizedAction = String(action ?? '').trim().toLowerCase();
        const localLines = cloneLines(this.mergeState.localLines);
        const serverLines = cloneLines(this.mergeState.serverLines);
        const ownerUserId = this.getSessionOwnerUserId() || this.ownerUserId;

        if (normalizedAction === 'keep-account-cart') {
            this.touchRevision(ownerUserId);
            this.applyServerCart(this.mergeState.serverCart, 'merge-keep-account', {
                ownerUserId,
                preserveRevision: true,
                version: this.mergeState.serverVersion,
            });
            this.mergeRequired = false;
            this.mergeState = null;
            this.notifySubscribers('merge-keep-account');
            if (shouldResumeCheckout) {
                await this.checkout();
            }
            return this.getSnapshot();
        }

        if (normalizedAction === 'keep-local-cart') {
            const serverCart = this.mergeState.serverCart;
            for (const serverLine of serverCart.lines) {
                if (serverLine.serverLineId) {
                    await this.removeServerLineById(serverLine.serverLineId, localLines);
                }
            }

            this.pendingServerRemovals.clear();
            this.mergeRequired = false;
            this.mergeState = null;
            this.syncState = CART_SYNC_STATES.DIRTY;
            this.touchRevision(ownerUserId);
            this.persistLines(localLines, CART_SYNC_STATES.DIRTY);
            await this.performSync('merge-keep-local', true);
            if (shouldResumeCheckout) {
                await this.checkout();
            }
            return this.getSnapshot();
        }

        const unionMap = mapLinesByKey(serverLines);
        localLines.forEach((line) => {
            unionMap.set(getCartLineKey(line), {
                ...unionMap.get(getCartLineKey(line)),
                ...line,
                assetPriceId: line.assetPriceId || unionMap.get(getCartLineKey(line))?.assetPriceId || null,
                display: line.display || unionMap.get(getCartLineKey(line))?.display || null,
                addedAt: line.addedAt || unionMap.get(getCartLineKey(line))?.addedAt || new Date().toISOString(),
            });
        });

        this.lines = Array.from(unionMap.values());
        this.mergeRequired = false;
        this.mergeState = null;
        this.syncState = CART_SYNC_STATES.DIRTY;
        this.touchRevision(ownerUserId);
        this.persistLines(this.lines, CART_SYNC_STATES.DIRTY);
        this.notifySubscribers('merge-keep-both');
        await this.performSync('merge-keep-both', true);
        if (shouldResumeCheckout) {
            await this.checkout();
        }

        return this.getSnapshot();
    }

    buildSecondaryDomainIntakeUrl(input) {
        const searchParams = new URLSearchParams();
        searchParams.set('type', input.type);
        searchParams.set('open', '1');
        searchParams.set('intent', 'buy');
        searchParams.set('marketplace_experiment', 'marketplace_v1');
        searchParams.set('marketplace_cohort', 'B');
        searchParams.set('origin_url', this.window.location.href);

        if (input.type === 'asset') {
            searchParams.set('asset_uuid', input.assetUuid);
            if (input.assetPriceId) {
                searchParams.set('asset_price_id', `${input.assetPriceId}`);
                searchParams.set('asset_price_source_type', 'asset_price');
            }
        }

        if (input.type === 'bundle') {
            searchParams.set('bundle_id', `${input.bundleId}`);
        }

        if (input.display?.name) {
            searchParams.set('name', input.display.name);
        }

        if (Number.isFinite(Number(input.display?.amount))) {
            searchParams.set('amount', `${input.display.amount}`);
        }

        if (input.display?.currency) {
            searchParams.set('currency', input.display.currency);
        }

        if (input.display?.vertical) {
            searchParams.set('vertical', input.display.vertical);
        }

        if (input.display?.previewUrl) {
            searchParams.set('preview_url', input.display.previewUrl);
        }

        if (input.display?.waveformUrl) {
            searchParams.set('waveform_url', input.display.waveformUrl);
        }

        return buildMarketplaceCartUrl(`/marketplace/cart/intake?${searchParams.toString()}`, this.window);
    }

    isPrimaryHost() {
        return isPrimaryProductioncrateHost(this.window.location.hostname);
    }

    canSyncInBackground() {
        return this.isPrimaryHost() && Boolean(this.session.user) && !this.session.isAuthenticating && !this.mergeRequired;
    }

    isCurrentAuthenticatedSession(userKey, sessionVersion) {
        const currentUserKey = this.session.user?.id || this.session.user?.email || null;

        return Boolean(userKey)
            && Boolean(this.session.user)
            && !this.session.isAuthenticating
            && currentUserKey === userKey
            && this.sessionVersion === sessionVersion;
    }

    clearSyncTimer() {
        if (this.syncTimer) {
            this.window.clearTimeout(this.syncTimer);
            this.syncTimer = null;
        }
    }

    queueSyncRequest(reason, forceSync = false) {
        this.pendingSyncRequest = {
            reason,
            forceSync: Boolean(forceSync) || Boolean(this.pendingSyncRequest?.forceSync),
        };
    }

    scheduleSync(reason, immediate = false) {
        if (!this.canSyncInBackground()) {
            return;
        }

        this.clearSyncTimer();
        this.syncTimer = this.window.setTimeout(() => {
            this.performSync(reason, false).catch((error) => {
                console.log('Background marketplace cart sync failed', error);
            });
        }, immediate ? 0 : SYNC_DEBOUNCE_MS);
    }

    async performSync(reason, forceSync) {
        this.queueSyncRequest(reason, forceSync);

        if (this.syncInFlightPromise) {
            await this.syncInFlightPromise;
            return this.getSnapshot();
        }

        this.syncInFlightPromise = (async () => {
            while (this.pendingSyncRequest) {
                const nextRequest = this.pendingSyncRequest;
                this.pendingSyncRequest = null;
                await this.runSyncPass(nextRequest.reason, nextRequest.forceSync);
            }
        })().finally(() => {
            this.syncInFlightPromise = null;
        });

        await this.syncInFlightPromise;
        return this.getSnapshot();
    }

    async runSyncPass(reason, forceSync) {
        if (!this.isPrimaryHost()) {
            return;
        }

        if (!this.session.user) {
            if (reason === 'checkout') {
                this.window.location.assign('/login?return_to=/marketplace/cart&cart_intent=checkout');
            }
            return;
        }

        if (this.mergeRequired && !forceSync) {
            return;
        }

        const syncCapture = this.captureSyncState();
        const desiredLineMap = mapLinesByKey(syncCapture.lines);

        this.syncState = CART_SYNC_STATES.SYNCING;
        this.dispatchEvent(EVENT_NAMES.SYNC_STATE, {
            syncState: this.syncState,
            reason,
        });
        this.notifySubscribers('sync-start');

        for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
                const currentServerEnvelope = await this.fetchServerCartEnvelope(syncCapture.lines);
                const currentServerCart = currentServerEnvelope.cart;
                if (!this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion)) {
                    return;
                }

                for (const serverLine of currentServerCart.lines) {
                    const lineKey = getCartLineKey(serverLine);
                    const desiredLine = desiredLineMap.get(lineKey);
                    const desiredSignature = getCartLineSignature(desiredLine);
                    const serverSignature = getCartLineSignature(serverLine);
                    const hasDisplayMetadataMismatch = hasServerDisplayMetadataMismatch(desiredLine, serverLine);
                    const shouldRemove = Boolean(serverLine.serverLineId)
                        && (
                            syncCapture.pendingServerRemovals.has(lineKey)
                            || !desiredLine
                            || desiredSignature !== serverSignature
                            || hasDisplayMetadataMismatch
                        );

                    if (shouldRemove) {
                        await this.removeServerLineById(serverLine.serverLineId, syncCapture.lines);
                        if (!this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion)) {
                            return;
                        }
                    }
                }

                const refreshedServerAfterDeletes = (await this.fetchServerCartEnvelope(syncCapture.lines)).cart;
                if (!this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion)) {
                    return;
                }
                const refreshedServerMap = mapLinesByKey(refreshedServerAfterDeletes.lines);

                for (const localLine of syncCapture.lines) {
                    const lineKey = getCartLineKey(localLine);
                    const localSignature = getCartLineSignature(localLine);
                    const serverLine = refreshedServerMap.get(lineKey);
                    const serverSignature = getCartLineSignature(serverLine);
                    const hasDisplayMetadataMismatch = hasServerDisplayMetadataMismatch(localLine, serverLine);

                    if (!serverLine || localSignature !== serverSignature || hasDisplayMetadataMismatch) {
                        if (localLine.type === 'asset') {
                            await this.addAssetLineToServer(localLine);
                        } else {
                            await this.addBundleLineToServer(localLine);
                        }
                        if (!this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion)) {
                            return;
                        }
                    }
                }

                const nextServerEnvelope = await this.fetchServerCartEnvelope(syncCapture.lines);
                const nextServerCart = nextServerEnvelope.cart;
                if (!this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion)) {
                    return;
                }

                const nextServerMap = mapLinesByKey(nextServerCart.lines);
                syncCapture.pendingServerRemovals.forEach((lineKey) => {
                    if (this.pendingServerRemovals.has(lineKey) && !nextServerMap.has(lineKey)) {
                        this.pendingServerRemovals.delete(lineKey);
                    }
                });

                if (!this.isCurrentSyncCapture(syncCapture)) {
                    this.queueSyncRequest(`${reason}-rerun`, forceSync);
                    return;
                }

                this.applyServerCart(nextServerCart, reason, {
                    preserveRevision: true,
                    ownerUserId: this.getSessionOwnerUserId() || this.ownerUserId,
                    version: nextServerEnvelope.version,
                });
                this.syncState = CART_SYNC_STATES.CLEAN;
                this.lastError = null;
                this.persistLines(this.lines, CART_SYNC_STATES.CLEAN);
                this.dispatchEvent(EVENT_NAMES.SYNC_STATE, {
                    syncState: this.syncState,
                    reason,
                });
                this.notifySubscribers('sync-success');
                return;
            } catch (error) {
                if (this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion) && this.revision !== syncCapture.revision) {
                    this.queueSyncRequest(`${reason}-rerun`, forceSync);
                    return;
                }

                const conflictEnvelope = this.applyVersionConflict(error, syncCapture.lines);
                if (conflictEnvelope && attempt === 0) {
                    continue;
                }

                if (this.isCurrentAuthenticatedSession(syncCapture.userKey, syncCapture.sessionVersion)) {
                    this.syncState = CART_SYNC_STATES.ERROR;
                    this.lastError = 'We could not sync yet. Please review the cart and try checkout again.';
                    this.persistLines(this.lines, CART_SYNC_STATES.ERROR);
                    this.dispatchEvent(EVENT_NAMES.SYNC_STATE, {
                        syncState: this.syncState,
                        reason,
                        error: this.lastError,
                    });
                    this.notifySubscribers('sync-error');
                }
                throw error;
            }
        }
    }

    async handleResolvedSession(resolvedUserKey, sessionVersion) {
        if (
            this.isClearingAfterCheckout
            || !this.isPrimaryHost()
            || !this.isCurrentAuthenticatedSession(resolvedUserKey, sessionVersion)
        ) {
            return;
        }

        const serverEnvelope = await this.fetchServerCartEnvelope(this.lines);
        const serverCart = serverEnvelope.cart;
        const resolvedOwnerUserId = this.getSessionOwnerUserId();

        if (this.isClearingAfterCheckout || !this.isCurrentAuthenticatedSession(resolvedUserKey, sessionVersion)) {
            return;
        }

        const localOwnerUserId = this.ownerUserId;
        const isSameAccountCookie = Boolean(resolvedOwnerUserId) && localOwnerUserId === resolvedOwnerUserId;
        const isForeignAccountCookie = Boolean(localOwnerUserId) && localOwnerUserId !== resolvedOwnerUserId;
        const isGuestCookie = !localOwnerUserId;
        const localIsDirty = [CART_SYNC_STATES.DIRTY, CART_SYNC_STATES.ERROR].includes(this.syncState);
        const cartsMatch = haveSameLineSet(this.lines, serverCart.lines);
        const serverLineMap = mapLinesByKey(serverCart.lines);
        const serverNeedsDisplayMetadata = this.lines.some((localLine) => (
            hasServerDisplayMetadataMismatch(localLine, serverLineMap.get(getCartLineKey(localLine)))
        ));

        if (isForeignAccountCookie) {
            this.touchRevision(resolvedOwnerUserId);
            this.applyServerCart(serverCart, 'server-refresh', {
                ownerUserId: resolvedOwnerUserId,
                preserveRevision: true,
                version: serverEnvelope.version,
            });
            this.notifySubscribers('server-refresh');
            return;
        }

        if (!this.lines.length && serverCart.lines.length) {
            this.touchRevision(resolvedOwnerUserId);
            this.applyServerCart(serverCart, 'server-hydrate', {
                ownerUserId: resolvedOwnerUserId,
                preserveRevision: true,
                version: serverEnvelope.version,
            });
            this.notifySubscribers('server-hydrate');
            return;
        }

        if (isSameAccountCookie) {
            if ((localIsDirty && !cartsMatch) || serverNeedsDisplayMetadata) {
                this.ownerUserId = resolvedOwnerUserId;
                await this.performSync('session-reconcile', true);
                return;
            }

            if (!cartsMatch || serverCart.lines.length || this.lines.length) {
                if (!cartsMatch) {
                    this.touchRevision(resolvedOwnerUserId);
                }
                this.applyServerCart(serverCart, 'server-refresh', {
                    ownerUserId: resolvedOwnerUserId,
                    preserveRevision: true,
                    version: serverEnvelope.version,
                });
                this.notifySubscribers('server-refresh');
                return;
            }

            this.ownerUserId = resolvedOwnerUserId;
            this.persistLines(this.lines, CART_SYNC_STATES.CLEAN);
            return;
        }

        if (isGuestCookie && !serverCart.lines.length && this.lines.length) {
            this.ownerUserId = resolvedOwnerUserId;
            await this.performSync('session-hydrate', true);
            return;
        }

        if (isGuestCookie && cartsMatch) {
            if (serverNeedsDisplayMetadata) {
                this.ownerUserId = resolvedOwnerUserId;
                await this.performSync('session-metadata-reconcile', true);
                return;
            }

            this.touchRevision(resolvedOwnerUserId);
            this.applyServerCart(serverCart, 'server-refresh', {
                ownerUserId: resolvedOwnerUserId,
                preserveRevision: true,
                version: serverEnvelope.version,
            });
            this.notifySubscribers('server-refresh');
            return;
        }

        if (shouldPromptMerge(this.lines, serverCart.lines)) {
            this.mergeRequired = true;
            this.mergeState = {
                localLines: cloneLines(this.lines),
                serverLines: cloneLines(serverCart.lines),
                serverCart,
                serverVersion: serverEnvelope.version,
            };
            this.setOpenState(true, 'merge-required');
            this.dispatchEvent(EVENT_NAMES.MERGE_REQUIRED, {
                localCount: this.lines.length,
                serverCount: serverCart.lines.length,
                snapshot: this.getSnapshot(),
            });
            this.notifySubscribers('merge-required');
            return;
        }

        if (!this.lines.length && !serverCart.lines.length) {
            this.ownerUserId = resolvedOwnerUserId;
            this.persistLines(this.lines, CART_SYNC_STATES.CLEAN);
            return;
        }

        if (!serverCart.lines.length && this.lines.length) {
            this.ownerUserId = resolvedOwnerUserId;
            await this.performSync('session-hydrate', true);
            return;
        }

        this.touchRevision(resolvedOwnerUserId);
        this.applyServerCart(serverCart, 'server-refresh', {
            ownerUserId: resolvedOwnerUserId,
            preserveRevision: true,
            version: serverEnvelope.version,
        });
        this.notifySubscribers('server-refresh');
    }

    createBroadcastChannel() {
        if (typeof this.window?.BroadcastChannel !== 'function') {
            return null;
        }

        const channel = new this.window.BroadcastChannel(CART_SYNC_CHANNEL_NAME);

        if (typeof channel.addEventListener === 'function') {
            channel.addEventListener('message', this.handleBroadcastMessage);
        } else {
            channel.onmessage = this.handleBroadcastMessage;
        }

        return channel;
    }

    handleBroadcastMessage(event) {
        this.handleIncomingCartMessage(event?.data);
    }

    handleStorageEvent(event) {
        if (event?.key !== CART_SYNC_STORAGE_KEY || !event?.newValue) {
            return;
        }

        try {
            this.handleIncomingCartMessage(JSON.parse(event.newValue));
        } catch (error) {
            return;
        }
    }

    handleIncomingCartMessage(message) {
        if (!message || message.sourceId === this.instanceWriterId) {
            return;
        }

        if (message.type === 'clear') {
            const shouldApply = compareSyncTuples(
                message.revision,
                message.writerId,
                this.revision,
                this.writerId
            ) > 0;

            if (!shouldApply) {
                return;
            }

            this.clientCartId = message.clientCartId || this.clientCartId;
            this.revision = Number.isInteger(message.revision) ? message.revision : this.revision;
            this.writerId = message.writerId || this.writerId;
            this.ownerUserId = null;
            this.syncState = CART_SYNC_STATES.CLEAN;
            this.lines = [];
            this.subtotal = calculateLocalSubtotal(this.lines);
            this.updatedAt = new Date().toISOString();
            this.pendingServerRemovals.clear();
            this.mergeRequired = false;
            this.mergeState = null;
            this.serverVersion = 0;
            this.lastError = null;
            this.notifySubscribers('remote-clear');
            return;
        }

        if (message.type !== 'state') {
            return;
        }

        const persistedState = decodeCartCookiePayload(message.encodedPayload);
        if (!persistedState) {
            return;
        }

        const shouldApply = compareSyncTuples(
            persistedState.revision,
            persistedState.writerId,
            this.revision,
            this.writerId
        ) > 0;

        if (!shouldApply) {
            return;
        }

        this.clientCartId = persistedState.clientCartId || this.clientCartId;
        this.revision = persistedState.revision;
        this.writerId = persistedState.writerId || this.writerId;
        this.ownerUserId = persistedState.ownerUserId || null;
        this.syncState = persistedState.syncState || CART_SYNC_STATES.CLEAN;
        this.lines = cloneLines(persistedState.lines).map((line) => ({
            ...line,
            serverLineId: null,
        }));
        this.subtotal = calculateLocalSubtotal(this.lines);
        this.updatedAt = new Date().toISOString();
        this.pendingServerRemovals.clear();
        this.mergeRequired = false;
        this.mergeState = null;
        this.serverVersion = 0;
        this.lastError = this.syncState === CART_SYNC_STATES.ERROR
            ? 'We could not sync yet. Please review the cart and try checkout again.'
            : null;
        this.notifySubscribers('remote-update');

        if (this.canSyncInBackground() && [CART_SYNC_STATES.DIRTY, CART_SYNC_STATES.ERROR].includes(this.syncState)) {
            this.scheduleSync('remote-update', true);
        }
    }

    publishCartMessage(message) {
        const normalizedMessage = {
            ...message,
            sourceId: this.instanceWriterId,
            messageId: uuidv4(),
        };

        if (this.broadcastChannel && typeof this.broadcastChannel.postMessage === 'function') {
            this.broadcastChannel.postMessage(normalizedMessage);
        }

        if (typeof this.window?.localStorage?.setItem === 'function') {
            this.window.localStorage.setItem(CART_SYNC_STORAGE_KEY, JSON.stringify(normalizedMessage));
        }
    }

    handleWindowFocus() {
        if ([CART_SYNC_STATES.DIRTY, CART_SYNC_STATES.ERROR].includes(this.syncState)) {
            this.scheduleSync('window-focus', true);
        }
    }

    handleWindowOnline() {
        if ([CART_SYNC_STATES.DIRTY, CART_SYNC_STATES.ERROR].includes(this.syncState)) {
            this.scheduleSync('window-online', true);
        }
    }

    applyServerCart(serverCart, reason, options = {}) {
        const { ownerUserId = this.ownerUserId, preserveRevision = false, version = this.serverVersion } = options;

        if (!preserveRevision) {
            this.touchRevision(ownerUserId);
        } else {
            this.ownerUserId = ownerUserId || null;
        }

        this.serverCart = serverCart;
        this.serverVersion = parseNonNegativeInteger(version);
        const localLineMap = mapLinesByKey(this.lines);
        this.lines = serverCart.lines.map((line) => {
            const localLine = localLineMap.get(getCartLineKey(line));

            return {
                ...line,
                assetHref: line.assetHref || localLine?.assetHref || null,
                addedAt: localLine?.addedAt || line.addedAt,
                display: mergeDisplay(line.display, localLine?.display),
            };
        });
        this.updatedAt = new Date().toISOString();
        this.subtotal = serverCart.subtotal.amount !== null
            ? serverCart.subtotal
            : calculateLocalSubtotal(this.lines);
        this.mergeRequired = false;
        this.mergeState = null;
        this.lastError = null;
        this.persistLines(this.lines, CART_SYNC_STATES.CLEAN);
        this.notifySubscribers(reason);
    }

    persistLines(nextLines, nextSyncState) {
        const nextUpdatedAt = new Date().toISOString();
        const nextPayload = buildPersistedCartPayload({
            clientCartId: this.clientCartId,
            ownerUserId: this.ownerUserId,
            revision: this.revision,
            syncState: nextSyncState,
            writerId: this.writerId,
            lines: nextLines,
        });
        const encodedPayload = encodeCartCookiePayload(nextPayload);

        if (encodedPayload.length > CART_COOKIE_SIZE_LIMIT) {
            return false;
        }

        writePersistedCartCookie(encodedPayload);
        this.updatedAt = nextUpdatedAt;
        this.publishCartMessage({
            type: 'state',
            clientCartId: this.clientCartId,
            revision: this.revision,
            writerId: this.writerId,
            encodedPayload,
        });
        return true;
    }

    setNonBlockingError(message) {
        this.lastError = message;
    }

    setOpenState(nextIsOpen, reason) {
        const normalizedIsOpen = Boolean(nextIsOpen);

        if (this.isOpen === normalizedIsOpen) {
            return false;
        }

        this.isOpen = normalizedIsOpen;
        this.dispatchEvent(normalizedIsOpen ? EVENT_NAMES.OPENED : EVENT_NAMES.CLOSED, {
            isOpen: this.isOpen,
            reason,
            snapshot: this.getSnapshot(),
        });
        return true;
    }

    notifySubscribers(reason) {
        const snapshot = this.getSnapshot();
        this.subscribers.forEach((listener) => {
            listener(snapshot);
        });

        this.dispatchEvent(EVENT_NAMES.CHANGED, {
            snapshot,
            reason,
        });
    }

    dispatchEvent(eventName, detail) {
        if (typeof this.window?.dispatchEvent !== 'function' || typeof this.window?.CustomEvent !== 'function') {
            return;
        }

        this.window.dispatchEvent(new this.window.CustomEvent(eventName, { detail }));
    }
}

let marketplaceCartControllerSingleton = null;

export const createMarketplaceCartController = (windowObject = window) => new MarketplaceCartController(windowObject);

export const getMarketplaceCartController = (windowObject = window) => {
    if (!marketplaceCartControllerSingleton) {
        marketplaceCartControllerSingleton = new MarketplaceCartController(windowObject);
    }

    return marketplaceCartControllerSingleton;
};

export const resetMarketplaceCartControllerForTests = () => {
    if (marketplaceCartControllerSingleton) {
        marketplaceCartControllerSingleton.destroy();
    }

    marketplaceCartControllerSingleton = null;
};

export const getEmptyCartSnapshot = () => buildEmptyCartSnapshot();

export const clearMarketplaceCartCookie = () => {
    clearPersistedCartCookies();
};
